// Background refresh of Twitch mod lists after auth (non-blocking for login)

import type { UserAuthFields } from '@airhead-games/shared/types';
import { getUserDetails } from '../transport/api/publicApi';
import {
  syncBlockedTerms,
  syncBlockedUsers,
} from '../transport/api/protectedApi';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('twitchModListRefresh');

/**
 * Refresh enabled Twitch mod lists in the background.
 * Returns a fresh user document after successful sync(s), or null.
 * Failures are swallowed so auth/session restore is never blocked.
 */
export async function refreshTwitchModListsInBackground(
  user: UserAuthFields
): Promise<UserAuthFields | null> {
  if (!user.authTwitch?.userId) {
    return null;
  }

  // Prefer fresh server flags — AuthContext can lag or omit fields.
  let sourceUser = user;
  try {
    const refreshed = await getUserDetails();
    if (refreshed.success && refreshed.data.user?.authTwitch?.userId) {
      sourceUser = refreshed.data.user;
    }
  } catch (error) {
    logger.warn(
      'Failed to reload user before Twitch mod-list sync; using in-memory user',
      error
    );
  }

  const syncUsers = !!sourceUser.modSettings?.disallowBlockedAndBanned;
  const syncTerms = !!sourceUser.modSettings?.twitchTermsBlocked;

  logger.info('Twitch mod-list refresh flags', {
    userId: sourceUser._id,
    syncUsers,
    syncTerms,
  });

  if (!syncUsers && !syncTerms) {
    return null;
  }

  // Kick each sync independently so one failure/path cannot skip the other.
  const results = await Promise.all([
    syncUsers
      ? syncBlockedUsers()
          .then((response) => !!response.data?.success)
          .catch((error) => {
            logger.warn('Background blocked-users sync failed', error);
            return false;
          })
      : Promise.resolve(false),
    syncTerms
      ? syncBlockedTerms()
          .then((response) => !!response.data?.success)
          .catch((error) => {
            logger.warn('Background blocked-terms sync failed', error);
            return false;
          })
      : Promise.resolve(false),
  ]);

  if (!results.some(Boolean)) {
    return null;
  }

  try {
    const refreshed = await getUserDetails();
    if (refreshed.success && refreshed.data.user) {
      return refreshed.data.user;
    }
  } catch (error) {
    logger.warn('Failed to reload user after Twitch mod-list sync', error);
  }

  return null;
}
